import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./AISolutions.css";
import SEO from "../../SEO";

const aiServices = [
  {
    title: "Generative AI & LLMs",
    description:
      "Custom AI chatbots and content generation tools trained on your business data to automate customer support and workflows.",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTmq3NYvfA7P7gQK80aQKNgagM9A7qJOQ3QQA&s",
    icon: "bi-cpu-fill",
    features: ["ERP Integration", "Secure Data Models", "Scalable AI Systems"],
  },
  {
    title: "Predictive Analytics",
    description:
      "Forecast sales, inventory demand, and customer behavior using advanced AI models.",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8i2KKVCS8p8ai5pus6ylZgn5nBIPPF1olDA&s",
    icon: "bi-graph-up-arrow",
    features: ["Real-time Insights", "Data Accuracy", "Business Forecasting"],
  },
  {
    title: "Computer Vision",
    description:
      "AI-powered image recognition for security, automation, and industrial use cases.",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRRfY_KdUZF-Z1mY2z4smOs0qSsZ07lLVj2Ug&s",
    icon: "bi-eye-fill",
    features: ["Smart Detection", "Automation", "High Accuracy"],
  },
  {
    title: "Business Automation",
    description:
      "AI-driven automation for invoices, reports, and repetitive business tasks.",
    img: "https://www.vertexplus.com/vertexplusadmin/uploads/blogs/main_image_166_1696592269.jpg",
    icon: "bi-gear-wide-connected",
    features: ["RPA Integration", "Time Saving", "Error Reduction"],
  },
];

function AISolutions() {
  useEffect(() => {
    AOS.init({ duration: 800, offset: 100, once: true });
  }, []);

  const features = [
    { icon: "🤖", title: "AI Chatbots" },
    { icon: "📊", title: "Predictive Analytics" },
    { icon: "👁️", title: "Computer Vision" },
    { icon: "⚙️", title: "Process Automation" },
  ];

  const benefits = [
    { icon: "⚡", title: "Faster Decision Making" },
    { icon: "💰", title: "Reduce Operational Cost" },
    { icon: "📈", title: "Increase Business Growth" },
    { icon: "🔐", title: "Secure AI Systems" },
  ];

  const industries = [
    "ERP Systems",
    "Retail & POS",
    "Pharmacies",
    "Warehouses",
    "E-Commerce",
    "Corporate Businesses",
  ];

  const faqs = [
    {
      q: "What are AI solutions in business?",
      a: "AI solutions automate tasks, improve decision-making and enhance business performance using machine learning and data analysis.",
    },
    {
      q: "Can AI integrate with ERP systems?",
      a: "Yes, our AI solutions fully integrate with ERP, POS and inventory systems.",
    },
  ];

  return (
    <>
      <SEO
        title="AI Solutions – MZR Software"
        description="Advanced AI, automation, predictive analytics and machine learning solutions for modern businesses."
        image="https://mzrsoftware.com/assets/images/og-aisolutions.jpg"
        url="https://mzrsoftware.com/OurServices/AISolutions/aisolutions"
      />

      <div className="ai-solutions-page">
        {/* HERO (H1 ADDED) */}
        <section className="ai-hero text-center">
          <div className="container">
            <h1 className="display-4 fw-bold text-white mb-3">
              MZR <span className="highlight">AI Intelligence Solutions</span>
            </h1>
            <p className="lead text-white mb-4">
              Empowering businesses with Artificial Intelligence, Automation,
              ERP intelligence, and predictive analytics systems.
            </p>
          </div>
        </section>

        {/* INTRODUCTION */}
        <section className="text-center mb-5" data-aos="fade-up">
          <h2 className="text-white">Introduction</h2>
          <p className="text-white-50 mx-auto" style={{ maxWidth: "900px" }}>
            Our AI solutions help businesses automate operations, analyze data,
            and improve decision-making using modern machine learning and ERP
            integration technologies.
          </p>
        </section>

        {/* FEATURES */}
        <section className="mb-5">
          <h2 className="text-center text-white mb-4" data-aos="fade-up">
            Features
          </h2>

          <div className="portfolio-grid">
            {features.map((f, i) => (
              <div
                key={i}
                className="portfolio-card text-center"
                data-aos="zoom-in"
              >
                <div className="card-content">
                  <div style={{ fontSize: "30px" }}>{f.icon}</div>
                  <h3>{f.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* BENEFITS */}
        <section className="mb-5">
          <h2 className="text-center text-white mb-4" data-aos="fade-up">
            Benefits
          </h2>

          <div className="portfolio-grid">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="portfolio-card text-center"
                data-aos="fade-up"
              >
                <div className="card-content">
                  <div style={{ fontSize: "30px" }}>{b.icon}</div>
                  <h3>{b.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* YOUR ORIGINAL DESIGN (UNCHANGED) */}
        <section className="services-section">
          <div className="container">
            <div className="services-grid">
              {aiServices.map((service, index) => (
                <div
                  key={index}
                  className={`service-card zig-zag ${
                    index % 2 === 0 ? "left" : "right"
                  }`}
                >
                  <div className="card-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <div className="service-features">
                      {service.features.map((feature, i) => (
                        <span key={i} className="feature-tag">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="card-image">
                    <img
                      src={service.img}
                      alt={service.title}
                      className="img-fluid rounded-4 shadow-lg"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="mb-5">
          <h2 className="text-center text-white mb-4" data-aos="fade-up">
            Industries
          </h2>

          <div className="portfolio-grid">
            {industries.map((item, i) => (
              <div
                key={i}
                className="portfolio-card text-center"
                data-aos="zoom-in"
              >
                <div className="card-content">
                  <h3>{item}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-5">
          <h2 className="text-center text-white mb-4" data-aos="fade-up">
            FAQs
          </h2>

          <div className="portfolio-grid">
            {faqs.map((f, i) => (
              <div key={i} className="portfolio-card" data-aos="fade-up">
                <div className="card-content">
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="portfolio-cta text-center mt-5" data-aos="zoom-in">
          <h2>Build AI Powered Business Solutions Today</h2>
          <p>
            Transform your ERP, POS and business systems with Artificial
            Intelligence.
          </p>
          <a href="/contact" className="btn-primary">
            Get Free Consultation →
          </a>
        </section>
      </div>
    </>
  );
}

export default AISolutions;
